import React from 'react';
import { X } from 'lucide-react';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '28px' }}>
    <h2 style={{
      fontSize: '14px',
      fontWeight: '700',
      color: '#1a1a1a',
      marginBottom: '10px',
      letterSpacing: '-0.01em'
    }}>
      {title}
    </h2>
    <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.7' }}>
      {children}
    </div>
  </div>
);

const TableRow_ = ({ label, value }) => (
  <tr>
    <td style={{
      padding: '8px 12px',
      fontSize: '12px',
      color: '#555',
      fontWeight: '500',
      borderBottom: '1px solid #e0ded9',
      width: '40%',
      verticalAlign: 'top'
    }}>
      {label}
    </td>
    <td style={{
      padding: '8px 12px',
      fontSize: '12px',
      color: '#444',
      borderBottom: '1px solid #e0ded9',
      verticalAlign: 'top'
    }}>
      {value}
    </td>
  </tr>
);

const PrivacyPolicy = ({ onClose }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: '40px 20px'
  }}>
    <div style={{
      background: '#fff',
      borderRadius: '10px',
      maxWidth: '760px',
      width: '100%',
      padding: '40px',
      position: 'relative',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      fontFamily: "'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif"
    }}>
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: '#f7f6f3',
          border: '1px solid #e0ded9',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#666'
        }}
        aria-label="Close privacy policy"
      >
        <X size={16} />
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #2b5ea7' }}>
        <h1 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#2b5ea7',
          margin: '0 0 6px 0',
          letterSpacing: '-0.01em'
        }}>
          Privacy &amp; Data Notice
        </h1>
        <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
          Loyal Sidekick · Operated by TK Strategies Limited, United Kingdom · Last updated: April 2026
        </p>
      </div>

      <Section title="1. Who We Are">
        <p>
          Loyal Sidekick is operated by TK Strategies Limited, a company registered in England and Wales.
          References to "we", "us", or "our" in this notice refer to TK Strategies Limited as the
          operator of this service.
        </p>
        <p style={{ marginTop: '8px' }}>
          For data enquiries, requests to exercise your rights, or complaints, contact us at:{' '}
          <a href="mailto:alan@tkstrat.net" style={{ color: '#2b5ea7' }}>alan@tkstrat.net</a>.
        </p>
      </Section>

      <Section title="2. What Data We Collect and Why">
        <p>When you use Loyal Sidekick, we collect the following categories of data:</p>
        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
          <li style={{ marginBottom: '6px' }}>
            <strong>Account data</strong> — your name and email address, provided via Clerk (our
            authentication provider), used to manage your account and enforce usage quotas.
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>Usage data</strong> — the number of analyses you have run this month, retained
            to manage your service quota and prevent abuse.
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>Analysis content</strong> — your problem statement, analytical persona selection,
            diagnostic responses, and the AI-generated output. This data is retained for up to
            30 days from creation for quality-control purposes, then automatically and permanently
            deleted. It is not used for any purpose beyond generating and temporarily holding
            your analysis output.
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>Service logs</strong> — anonymised usage records (action type, token volume)
            retained for up to 12 months for billing verification and service monitoring. These
            logs do not contain your problem statement or output content.
          </li>
        </ul>
        <p style={{ marginTop: '10px' }}>
          We do not collect payment information directly. We do not use tracking cookies or
          behavioural advertising technologies.
        </p>
      </Section>

      <Section title="3. Legal Basis for Processing (UK GDPR)">
        <p>We process your personal data on the following bases under UK GDPR:</p>
        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
          <li style={{ marginBottom: '6px' }}>
            <strong>Contract performance (Article 6(1)(b))</strong> — processing your inputs and
            delivering analysis outputs is necessary to provide the service you have requested.
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>Legitimate interest (Article 6(1)(f))</strong> — retaining anonymised usage
            logs for service quality, abuse prevention, and improvement. We have assessed that
            this interest does not override your rights and freedoms.
          </li>
        </ul>
      </Section>

      <Section title="4. How Loyal Sidekick Uses Claude (AI Processing)">
        <p>
          Loyal Sidekick is powered by <strong>Claude</strong>, an AI system developed by Anthropic,
          PBC (<a href="https://www.anthropic.com" target="_blank" rel="noopener noreferrer"
          style={{ color: '#2b5ea7' }}>anthropic.com</a>). When you run an analysis, your inputs —
          including your problem statement and diagnostic responses — are transmitted to Anthropic's
          API to generate a response. Anthropic acts as a data processor on our behalf.
        </p>
        <p style={{ marginTop: '10px', padding: '12px 16px', background: '#f0f5ff', borderRadius: '6px',
          borderLeft: '3px solid #2b5ea7' }}>
          <strong>Anthropic does not use data processed through its commercial API to train its
          AI models.</strong> Anthropic may retain API inputs and outputs for a short period
          (up to 30 days) for safety monitoring purposes only, after which they are automatically
          deleted. This is independent of, and in addition to, our own 30-day retention window.
        </p>
        <p style={{ marginTop: '10px' }}>
          <strong>Web search.</strong> During the analysis execution stage, the service uses
          Anthropic's real-time web search capability to ground responses in current information.
          Your problem context is used as input to web searches conducted by the AI model.
          No personally identifiable information beyond your problem statement is transmitted
          to search infrastructure, and search queries are not linked to your account by
          third-party search providers.
        </p>
        <p style={{ marginTop: '10px' }}>
          Anthropic's processing is governed by their Data Processing Agreement, which incorporates
          Standard Contractual Clauses (SCCs) for transfers of personal data to the United States.
        </p>
      </Section>

      <Section title="5. Data Retention">
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px',
          border: '1px solid #e0ded9', borderRadius: '6px', overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: '#f7f6f3' }}>
              <th style={{ padding: '10px 12px', fontSize: '11px', fontWeight: '600',
                color: '#555', textAlign: 'left', borderBottom: '1px solid #e0ded9',
                letterSpacing: '0.04em', textTransform: 'uppercase' }}>Data category</th>
              <th style={{ padding: '10px 12px', fontSize: '11px', fontWeight: '600',
                color: '#555', textAlign: 'left', borderBottom: '1px solid #e0ded9',
                letterSpacing: '0.04em', textTransform: 'uppercase' }}>Retention period</th>
            </tr>
          </thead>
          <tbody>
            <TableRow_ label="Analysis content (problem statement, persona, output, indicators)"
              value="30 days from creation, then permanently and automatically deleted" />
            <TableRow_ label="Account profile (name, email, usage quota)"
              value="Duration of account, or until you request deletion" />
            <TableRow_ label="Anonymised usage logs (action type, token counts)"
              value="12 months" />
            <TableRow_ label="Anthropic API inputs/outputs (safety monitoring, Anthropic's systems)"
              value="Up to 30 days (Anthropic; not used for training)" />
          </tbody>
        </table>
      </Section>

      <Section title="6. International Data Transfers">
        <p>
          Anthropic processes API data in the United States. This transfer is covered by Standard
          Contractual Clauses incorporated into Anthropic's commercial terms. Clerk (authentication)
          and Supabase (database hosting) may also process data outside the United Kingdom; each
          maintains appropriate UK GDPR-compliant transfer safeguards. We will provide further
          detail on these safeguards upon request.
        </p>
      </Section>

      <Section title="7. A Note on Sensitive Inputs — Please Read Carefully">
        <div style={{
          background: '#fff8f0',
          border: '1px solid #f0c070',
          borderLeft: '3px solid #e8a020',
          borderRadius: '6px',
          padding: '14px 16px'
        }}>
          <p style={{ margin: 0, fontWeight: '600', color: '#8a5a00', marginBottom: '8px' }}>
            Do not submit any of the following through this service:
          </p>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            {[
              'Personally identifiable information about individuals who have not consented to its use in an AI-powered service',
              'Legally privileged communications or confidential legal advice',
              'Commercially sensitive information — trade secrets, unpublished financial data, unreleased product plans — whose disclosure beyond your organisation could cause harm',
              'Information classified under any government, military, or organisational security framework',
              'Health, financial, or biometric data relating to identifiable individuals',
              'Information subject to a non-disclosure agreement or regulatory restriction',
            ].map((item, i) => (
              <li key={i} style={{ marginBottom: '5px', color: '#8a5a00' }}>{item}</li>
            ))}
          </ul>
          <p style={{ marginTop: '10px', margin: '10px 0 0 0', color: '#8a5a00', fontSize: '12px' }}>
            Your inputs transit third-party infrastructure. While they are not retained for training
            and are deleted within 30 days, you are responsible for ensuring any material you submit
            is appropriate for transmission through an externally-hosted AI service.
            <strong> If your professional context requires analysis of sensitive materials, anonymise
            or paraphrase your inputs before submission.</strong>
          </p>
        </div>
      </Section>

      <Section title="8. AI-Generated Content — Important Limitation">
        <p>
          Analysis produced by Loyal Sidekick is generated by an AI system and may be incomplete,
          inaccurate, biased, or not reflective of the most current events or data. Outputs are
          shaped by the model's training data, your chosen analytical persona, and web search
          results at the time of generation — all of which carry inherent limitations and potential
          for error.
        </p>
        <p style={{ marginTop: '10px' }}>
          <strong>Loyal Sidekick does not provide professional legal, financial, regulatory, or
          strategic advice.</strong> Outputs should not be relied upon as a substitute for independent
          professional judgement or formal expert advice. Before acting on any analysis, verify
          significant conclusions through appropriate professional and primary sources.
        </p>
      </Section>

      <Section title="9. Your Rights">
        <p>
          Under UK GDPR and applicable data protection law, you have the right to:
        </p>
        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
          {[
            ['Access', 'request a copy of the personal data we hold about you'],
            ['Rectification', 'request correction of inaccurate or incomplete data'],
            ['Erasure', 'request deletion of your personal data ("right to be forgotten")'],
            ['Restriction', 'request that we limit processing of your data in certain circumstances'],
            ['Object', 'object to processing based on legitimate interest'],
            ['Portability', 'receive data you have provided in a structured, machine-readable format'],
          ].map(([right, desc]) => (
            <li key={right} style={{ marginBottom: '6px' }}>
              <strong>{right}</strong> — {desc}
            </li>
          ))}
        </ul>
        <p style={{ marginTop: '10px' }}>
          To exercise any right, contact{' '}
          <a href="mailto:alan@tkstrat.net" style={{ color: '#2b5ea7' }}>alan@tkstrat.net</a>.
          We will respond within 30 days. You also have the right to lodge a complaint with the
          UK Information Commissioner's Office:{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer"
            style={{ color: '#2b5ea7' }}>ico.org.uk</a>.
        </p>
        <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
          <strong>Users in other jurisdictions:</strong> If you are located in the European Economic
          Area, you may also have rights under EU GDPR and may lodge a complaint with your local
          supervisory authority. California residents have additional rights under CCPA/CPRA,
          including the right to know, delete, and opt-out of sale of personal information.
          We do not sell personal information. Contact us to exercise any jurisdiction-specific rights.
        </p>
      </Section>

      <Section title="10. Changes to This Notice">
        <p>
          We may update this notice from time to time to reflect changes in our practices, legal
          obligations, or the services we use. The "last updated" date at the top will reflect
          any material changes. Continued use of the service after an update constitutes
          acceptance of the revised notice. For significant changes, we will notify active users
          by email where practicable.
        </p>
      </Section>

      {/* Footer close button */}
      <div style={{ textAlign: 'center', marginTop: '32px', paddingTop: '20px',
        borderTop: '1px solid #e0ded9' }}>
        <button
          onClick={onClose}
          style={{
            background: '#2b5ea7',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 28px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            letterSpacing: '0.02em'
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
